import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { AbsorbanceTable } from "./types";

interface SpectrophotometerState {
  lidOpen: boolean;
  poweredUp: boolean;
  wavelength: number;
  absorbanceValue: number;
  relativeAbsorbance: number;
  error: number;
  chosenWavelength?: number;
  absorbanceTable: AbsorbanceTable;
  troubleshootDone: boolean;
}

const initialState: SpectrophotometerState = {
  lidOpen: false,
  poweredUp: false,
  wavelength: 350,
  absorbanceValue: 0,
  relativeAbsorbance: 0,
  error: 0,
  troubleshootDone: false,
  absorbanceTable: {
    indices: [5, 2],
    data: [
      [
        { type: "TITLE", text: "Samples" },
        { type: "TITLE", text: "Absorbance (400 nm)" },
      ],
      [
        { type: "TITLE", text: "Substance S", value: 7 },
        {
          type: "INPUT",
          placeholder: "Enter value",
          disabled: false,
          inputType: "number",
          accuracy: 2,
        },
      ],
    ],
  },
};

const dilutedSampleRow = [
  { type: "FIXED" as const, text: "Diluted sample", value: 7 },
  {
    type: "INPUT" as const,
    placeholder: "Enter value",
    inputType: "number",
    disabled: false,
    accuracy: 2,
    linkedField: {
      section: "Results",
      componentId: 12,
      rowId: 2,
      cellId: 2,
    },
  },
];

const randomBetween = (min: number, max: number) => {
  return Math.random() * (max - min) + min;
};

const calculateAbsorbance = (
  cuvetteId: string,
  wavelength: number,
  relativeAbsorbance: number,
  diluted: boolean
) => {
  let absorbanceValue = 0.25;
  let randomizationThreshold = 0.1;

  if (["cuvette_2", "cuvette_4"].includes(cuvetteId)) {
    absorbanceValue =
      wavelength > 400
        ? (0.8 * (700 - wavelength)) / 300
        : 0.8 + 0.012 * (wavelength - 400);

    if (!diluted) {
      absorbanceValue = absorbanceValue + 1 + relativeAbsorbance;
      randomizationThreshold = 0.2;
    }
  }
  if (cuvetteId === "cuvette_3") {
    absorbanceValue = -randomizationThreshold;
  }
  let newAbsorbanceValue = randomBetween(
    absorbanceValue - randomizationThreshold,
    absorbanceValue + randomizationThreshold
  );

  const min = cuvetteId === "cuvette_3" ? -0.8 : 0.1;
  let max = cuvetteId === "cuvette_1" ? 0.5 : 2.3;
  max = cuvetteId === "cuvette_4" ? 0.98 : max;

  newAbsorbanceValue =
    newAbsorbanceValue - relativeAbsorbance < min
      ? min + relativeAbsorbance
      : newAbsorbanceValue;
  newAbsorbanceValue =
    newAbsorbanceValue - relativeAbsorbance > max
      ? max + relativeAbsorbance
      : newAbsorbanceValue;

  return newAbsorbanceValue;
};
const spectrophotometerSlice = createSlice({
  name: "spectrophotometer",
  initialState,
  reducers: {
    absorbanceTableCellUpdateAction(
      state,
      {
        payload: { row, column, value },
      }: PayloadAction<{
        row: number;
        column: number;
        value: number | string;
      }>
    ) {
      state.absorbanceTable.data[row][column].value = value;
    },
    recalculateAbsorbance(
      state,
      {
        payload: { cuvetteId, diluted },
      }: PayloadAction<{
        cuvetteId: string;
        diluted: boolean;
      }>
    ) {
      if (cuvetteId) {
        if (cuvetteId === "cuvette_1" && state.error) {
          state.absorbanceValue = state.error;
        } else {
          const newAbsorbanceValue =
            calculateAbsorbance(
              cuvetteId,
              state.wavelength,
              state.relativeAbsorbance,
              diluted
            ) || 0;
          state.absorbanceValue = newAbsorbanceValue;
          if (cuvetteId === "cuvette_1") {
            state.error = newAbsorbanceValue;
          }
        }
        if (diluted && state.absorbanceTable.data.length < 3) {
          state.absorbanceTable.data.push(dilutedSampleRow);
        }
      }
      if (diluted && state.absorbanceTable.data.length < 3) {
        state.absorbanceTable.data.push(dilutedSampleRow);
      }
    },
    setWavelengthAction(
      state,
      {
        payload: { wavelength, cuvetteId, diluted },
      }: PayloadAction<{
        wavelength: number;
        cuvetteId: string;
        diluted: boolean;
      }>
    ) {
      state.wavelength = wavelength;
      if (cuvetteId) {
        state.absorbanceValue =
          calculateAbsorbance(
            cuvetteId,
            wavelength,
            state.relativeAbsorbance,
            diluted
          ) || 0;
      }
    },
    zeroAction(state) {
      state.absorbanceValue = 0;
      state.relativeAbsorbance = state.absorbanceValue;
    },
    choseWavelengthMatchedAction() {
      return;
    },
    powerToggleAction(state) {
      state.poweredUp = !state.poweredUp;
    },
    setChosenWavelengthAction(
      state,
      { payload: wavelength }: PayloadAction<number | undefined>
    ) {
      state.chosenWavelength = wavelength;
    },
    troubleshootDone(state) {
      state.troubleshootDone = true;
    },
    absorbanceUpdated(state, action: { payload: number }) {
      state.absorbanceValue = action.payload;
    },
    whatIsLightAnsweredAction() {
      return;
    },
  },
});

export const useSpectrophotometer = () =>
  useSelector(spectrophotometerSlice.selectSlice);
export const {
  actions: {
    absorbanceTableCellUpdateAction,
    setWavelengthAction,
    setChosenWavelengthAction,
    powerToggleAction,
    zeroAction,
    choseWavelengthMatchedAction,
    troubleshootDone,
    recalculateAbsorbance,
    absorbanceUpdated,
    whatIsLightAnsweredAction,
  },
  reducer: SpectrophotometerReducer,
} = spectrophotometerSlice;
